import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { Employee } from '@/types/employee';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { RoleAssignmentSection } from './RoleAssignmentSection';

interface EmployeeApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: Employee | null;
  onApprovalChange: () => void;
}

export function EmployeeApprovalDialog({ 
  open, 
  onOpenChange, 
  employee, 
  onApprovalChange 
}: EmployeeApprovalDialogProps) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const { user } = useAuth();

  if (!employee) return null;

  const getApprovalStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          icon: <Clock className="w-4 h-4" />,
          label: 'في انتظار الموافقة',
          color: 'bg-warning/20 text-warning border-warning/30'
        };
      case 'approved':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          label: 'تمت الموافقة',
          color: 'bg-success/20 text-success border-success/30'
        };
      case 'rejected':
        return {
          icon: <XCircle className="w-4 h-4" />,
          label: 'مرفوض',
          color: 'bg-destructive/20 text-destructive border-destructive/30'
        };
      default:
        return {
          icon: <AlertTriangle className="w-4 w-4" />,
          label: 'غير محدد',
          color: 'bg-muted text-muted-foreground'
        };
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleApproval = async (action: 'approve' | 'reject') => {
    if (!user?.employeeData?.id) {
      toast.error('لا يمكن تحديد هوية المدير');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc(
        action === 'approve' ? 'approve_employee' : 'reject_employee',
        {
          employee_id_param: employee.id,
          approver_id_param: user.employeeData.id,
          ...(action === 'reject' && { reason_param: rejectionReason })
        }
      );

      if (error) {
        console.error(`Error ${action}ing employee:`, error);
        toast.error(`فشل في ${action === 'approve' ? 'الموافقة على' : 'رفض'} الموظف`);
        return;
      }

      if (data === false) {
        toast.error('ليس لديك صلاحية لتنفيذ هذا الإجراء');
        return;
      }

      // إذا تمت الموافقة وتم اختيار أدوار، قم بإضافتها
      if (action === 'approve' && selectedRoles.length > 0) {
        for (const roleId of selectedRoles) {
          const { error: roleError } = await supabase
            .from('employee_roles')
            .insert({
              employee_id: employee.id,
              role_id: roleId,
              assigned_by: user.employeeData.id
            });

          if (roleError) {
            console.error('Error assigning role:', roleError);
          }
        }
      }

      toast.success(`تم ${action === 'approve' ? 'الموافقة على' : 'رفض'} الموظف بنجاح`);
      onApprovalChange();
      onOpenChange(false);
      setRejectionReason('');
      setSelectedRoles([]);
    } catch (error) {
      console.error(`Error ${action}ing employee:`, error);
      toast.error(`حدث خطأ أثناء ${action === 'approve' ? 'الموافقة على' : 'رفض'} الموظف`);
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = getApprovalStatusInfo(employee.approval_status || 'pending');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-primary" />
            موافقة على الموظف
          </DialogTitle>
          <DialogDescription>
            إدارة حالة الموافقة للموظف الجديد
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* معلومات الموظف */}
          <div className="bg-muted/30 p-4 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">معلومات الموظف</h3>
              <Badge className={`${statusInfo.color} text-xs`}>
                {statusInfo.icon}
                <span className="mr-1">{statusInfo.label}</span>
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">الاسم:</span>
                <p className="font-medium">{employee.name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">البريد الإلكتروني:</span>
                <p className="font-medium">{employee.email || 'غير محدد'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">المنصب:</span>
                <p className="font-medium">{employee.position}</p>
              </div>
              <div>
                <span className="text-muted-foreground">القسم:</span>
                <p className="font-medium">{employee.department}</p>
              </div>
            </div>

            {employee.rejection_reason && (
              <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded">
                <span className="text-destructive text-sm font-medium">سبب الرفض:</span>
                <p className="text-sm text-destructive/80 mt-1">{employee.rejection_reason}</p>
              </div>
            )}
          </div>

          {/* تعيين الصلاحيات */}
          {employee.approval_status === 'pending' && (
            <div className="bg-muted/30 p-4 rounded-lg">
              <RoleAssignmentSection 
                selectedRoles={selectedRoles}
                onRoleToggle={handleRoleToggle}
              />
            </div>
          )}

          {/* إجراءات الموافقة */}
          {employee.approval_status === 'pending' && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <Button
                  onClick={() => handleApproval('approve')}
                  disabled={loading}
                  className="flex-1 bg-success hover:bg-success/90 text-white"
                >
                  <CheckCircle className="w-4 h-4 ml-2" />
                  موافقة
                </Button>
                <Button
                  onClick={() => handleApproval('reject')}
                  disabled={loading}
                  variant="outline"
                  className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
                >
                  <XCircle className="w-4 h-4 ml-2" />
                  رفض
                </Button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  سبب الرفض (اختياري)
                </label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="اكتب سبب رفض الموظف..."
                  className="min-h-[80px] resize-none"
                />
              </div>
            </div>
          )}

          {/* معلومات الموافقة */}
          {employee.approved_at && (
            <div className="bg-muted/30 p-3 rounded text-sm">
              <span className="text-muted-foreground">
                تم {employee.approval_status === 'approved' ? 'الموافقة' : 'الرفض'} في: {' '}
              </span>
              <span className="font-medium">
                {new Date(employee.approved_at).toLocaleDateString('ar-SA')}
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}